/**
 * Single media loader replacing the legacy LazyImage / ImageWithUrl /
 * VideoWithUrl / ProgressiveImage. Resolves a presigned URL via the shared
 * cache, lazy-loads via IntersectionObserver unless `priority`, and surfaces a
 * real error state with a retry button (no silent placeholder swap).
 */
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { IconReload, IconPhotoOff, IconPlayerPlayFilled } from '@tabler/icons-react';
import { useSignedUrl } from '../../hooks/useSignedUrl.ts';
import { cn } from '../../lib/cn.ts';

interface MediaProps {
  /** S3 key to resolve (thumbnail key for grid, original for full view). */
  fileKey: string;
  kind: 'image' | 'video' | 'other';
  alt: string;
  className?: string;
  /** Skip lazy-loading and fetch immediately (above-the-fold / lightbox). */
  priority?: boolean;
  /** Render a play badge over images used as video posters. */
  videoBadge?: boolean;
  /** Full-resolution video playback (lightbox) instead of a poster image. */
  playable?: boolean;
  /** How the image fits its box. Default 'cover'. */
  fit?: 'cover' | 'contain';
  /** Reported once the image decodes — used to measure aspect ratio. */
  onNaturalSize?: (width: number, height: number) => void;
}

export function Media({
  fileKey,
  kind,
  alt,
  className,
  priority = false,
  videoBadge = false,
  playable = false,
  fit = 'cover',
  onNaturalSize,
}: MediaProps) {
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '200px' });
  const enabled = priority || inView;
  const { url, loading, error, retry } = useSignedUrl(fileKey, enabled);
  const [loaded, setLoaded] = useState(false);

  if (error) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center gap-2 bg-grid text-fg-2',
          className,
        )}
      >
        <IconPhotoOff className="h-7 w-7" />
        <button
          onClick={retry}
          className="inline-flex items-center gap-1 rounded-lg bg-accent px-2 py-1 text-xs font-medium text-white transition-colors duration-200 ease-standard hover:bg-accent-hover"
        >
          <IconReload className="h-3.5 w-3.5" /> Retry
        </button>
      </div>
    );
  }

  if (!url || loading) {
    return <div ref={ref} className={cn('skeleton', className)} aria-label={`Loading ${alt}`} />;
  }

  if (kind === 'video' && playable) {
    // User-uploaded videos have no caption track available.
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video src={url} controls autoPlay className={className} />;
  }

  // Contain mode (lightbox): the size constraints (max-h/max-w) belong on the
  // <img> so a tall portrait scales down to fit instead of overflowing. The img
  // sizes to its natural aspect within those bounds — no fixed parent height.
  if (fit === 'contain') {
    return (
      <div ref={ref} className="relative flex items-center justify-center">
        <img
          src={url}
          alt={alt}
          onLoad={(e) => {
            setLoaded(true);
            onNaturalSize?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight);
          }}
          className={cn(
            'h-auto w-auto object-contain transition-[opacity,transform] duration-500 ease-out',
            className,
            loaded ? 'scale-100 opacity-100 blur-0' : 'scale-[1.02] opacity-0 blur-md',
          )}
        />
      </div>
    );
  }

  // Cover mode (grid tiles): fill the tile box.
  return (
    <div ref={ref} className={cn('relative overflow-hidden', className)}>
      <img
        src={url}
        alt={alt}
        loading="lazy"
        onLoad={(e) => {
          setLoaded(true);
          const img = e.currentTarget;
          onNaturalSize?.(img.naturalWidth, img.naturalHeight);
        }}
        className={cn(
          'h-full w-full object-cover transition-[opacity,transform] duration-500 ease-out',
          loaded ? 'scale-100 opacity-100 blur-0' : 'scale-105 opacity-0 blur-md',
        )}
      />
      {videoBadge && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="grid h-12 w-12 place-items-center rounded-full glass-dark text-white ring-1 ring-white/30">
            <IconPlayerPlayFilled className="h-6 w-6" />
          </span>
        </div>
      )}
    </div>
  );
}
